<script setup>
import Layout from '@/Layouts/Layout.vue';
import { Link } from '@inertiajs/vue3';
import { CheckIcon } from '@heroicons/vue/20/solid';
import {
    DevicePhoneMobileIcon,
    PaintBrushIcon,
    CurrencyDollarIcon,
    ClipboardDocumentIcon,
    MapPinIcon,
    UsersIcon
} from '@heroicons/vue/24/outline';
import PrimaryButton from '@/Components/PrimaryButton.vue';
import LoginButtons from './Partials/LoginButtons.vue';
</script>

<template>
    <Layout :isGuest="true">
        <div class="w-full mx-auto">
            <div class="w-full rounded-sm bg-white p-4 pt-6 pb-24 md:p-12 md:pb-24 flex flex-col">
                <Link :href="route('landing.index')" class="mx-auto w-32">
                    <img :src="asset('img/logo.svg')" alt="WedPro Logo" class="w-full" />
                </Link>
                <div class="md:flex md:gap-8 md:mt-16">
                    <div class="md:w-1/2">
                        <img
                            cy-test="landing-page-image"
                            class="mt-4 md:hidden w-full px-2"
                            :src="asset('img/landingpages/smiling-bride-landscape.jpg')"
                        />
                        <img
                            cy-test="landing-page-image"
                            class="hidden md:block mt-0 px-4 w-full"
                            :src="asset('img/landingpages/smiling-bride-portrait.jpg')"
                        />
                    </div>
                    <div class="md:w-1/2 px-2 md:px-0 mt-4 md:mt-0">
                        <h1 cy-test="landing-page-title" class="uppercase font-serif text-2xl text-center md:text-left">
                            Wedding Planner for Kiwi Brides
                        </h1>
                        <div class="mt-6 flex justify-start">
                            <CheckIcon class="text-teal-500 w-5 h-5 mr-2 shrink-0" />
                            Easily plan your dream wedding
                        </div>
                        <div class="mt-2 flex justify-start">
                            <CheckIcon class="text-teal-500 w-5 h-5 mr-2 shrink-0" />
                            Works across all your devices (use it on the go on your phone)
                        </div>
                        <div class="mt-2 flex justify-start">
                            <CheckIcon class="text-teal-500 w-5 h-5 mr-2 shrink-0" />
                            Full NZ wedding todo list & budget template
                        </div>
                        <h2
                            class="font-serif text-xl mt-8 md:mt-6 mb-3 text-dusty-pink-medium font-bold text-center md:text-left"
                        >
                            Grab yours for free:
                        </h2>
                        <LoginButtons :landing="'planner'" :hideRegistrationLink="true"></LoginButtons>
                    </div>
                </div>

                <div class="relative w-full px-6 mt-16 pt-20 pb-12">
                    <div class="relative">
                        <div class="relative">
                            <svg
                                class="absolute top-0 left-0 h-36 w-36 -translate-x-8 -translate-y-24 transform text-rose-200 opacity-50"
                                stroke="currentColor"
                                fill="none"
                                viewBox="0 0 144 144"
                                aria-hidden="true"
                            >
                                <path
                                    stroke-width="2"
                                    d="M41.485 15C17.753 31.753 1 59.208 1 89.455c0 24.664 14.891 39.09 32.109 39.09 16.287 0 28.386-13.03 28.386-28.387 0-15.356-10.703-26.524-24.663-26.524-2.792 0-6.515.465-7.446.93 2.327-15.821 17.218-34.435 32.11-43.742L41.485 15zm80.04 0c-23.268 16.753-40.02 44.208-40.02 74.455 0 24.664 14.891 39.09 32.109 39.09 15.822 0 28.386-13.03 28.386-28.387 0-15.356-11.168-26.524-25.129-26.524-2.792 0-6.049.465-6.98.93 2.327-15.821 16.753-34.435 31.644-43.742L121.525 15z"
                                />
                            </svg>
                            <blockquote class="relative">
                                <div class="text-2xl font-medium leading-9 text-gray-900">
                                    <p>
                                        Want planning your wedding to be a simple and beautiful experience? Then this planner is
                                        for you. We created it with help from real Kiwi brides, so we're confident you'll love
                                        using it.
                                    </p>
                                </div>
                                <footer class="mt-8">
                                    <div class="flex">
                                        <div class="shrink-0">
                                            <img
                                                class="h-12 w-12 rounded-full"
                                                :src="asset('img/landingpages/nikkiandjosh.jpg')"
                                                alt="photo of founders"
                                            />
                                        </div>
                                        <div class="ml-4">
                                            <div class="text-base font-medium text-gray-900">Nikki & Josh Trebilco</div>
                                            <div class="text-base font-medium text-rose-300">Founders, WedPro</div>
                                        </div>
                                    </div>
                                </footer>
                            </blockquote>
                        </div>
                    </div>
                </div>

                <div class="w-full text-center mt-12">
                    <Link :href="route('register')">
                        <PrimaryButton class="text-xl">Plan your dream wedding &rarr;</PrimaryButton>
                    </Link>
                </div>

                <div class="mt-24 w-full bg-white shadow-xl rounded-lg px-6 py-24 border border-gray-200">
                    <h2 class="text-4xl text-rose-300 font-bold tracking-tight">Planner Features</h2>
                    <div class="mt-8 grid grid-cols-1 gap-16">
                        <div>
                            <div>
                                <span class="flex h-12 w-12 items-center justify-center rounded-xl bg-white/10">
                                    <DevicePhoneMobileIcon class="text-gray-400 h-8 w-8 shrink-0" />
                                </span>
                            </div>
                            <div class="mt-6">
                                <h3 class="text-lg font-semibold leading-8 text-gray-400">Mobile Friendly</h3>
                                <p class="mt-2 text-base leading-7 text-gray-400">
                                    Use your wedding planner on the go on your phone (or on a laptop at a cafe!)
                                </p>
                            </div>
                        </div>

                        <div>
                            <div>
                                <span class="flex h-12 w-12 items-center justify-center rounded-xl bg-white/10">
                                    <PaintBrushIcon class="text-gray-400 h-8 w-8 shrink-0" />
                                </span>
                            </div>
                            <div class="mt-6">
                                <h3 class="text-lg font-semibold leading-8 text-gray-400">Personalized Colours</h3>
                                <p class="mt-2 text-base leading-7 text-gray-400">
                                    Choose your own theme colour for a beautifully personalized planner.
                                </p>
                            </div>
                        </div>

                        <div>
                            <div>
                                <span class="flex h-12 w-12 items-center justify-center rounded-xl bg-white/10">
                                    <CurrencyDollarIcon class="text-gray-400 h-8 w-8 shrink-0" />
                                </span>
                            </div>
                            <div class="mt-6">
                                <h3 class="text-lg font-semibold leading-8 text-gray-400">Template Budget</h3>
                                <p class="mt-2 text-base leading-7 text-gray-400">
                                    Easily keep track of your wedding budget with our user friendly NZ wedding budget template.
                                </p>
                            </div>
                        </div>

                        <div>
                            <div>
                                <span class="flex h-12 w-12 items-center justify-center rounded-xl bg-white/10">
                                    <ClipboardDocumentIcon class="text-gray-400 h-8 w-8 shrink-0" />
                                </span>
                            </div>
                            <div class="mt-6">
                                <h3 class="text-lg font-semibold leading-8 text-gray-400">Wedding Checklist</h3>
                                <p class="mt-2 text-base leading-7 text-gray-400">
                                    Add, edit, delete and write notes on your wedding todo list. We include a comprehensive NZ
                                    wedding todo list to ensure you don't forget anything.
                                </p>
                            </div>
                        </div>

                        <div>
                            <div>
                                <span class="flex h-12 w-12 items-center justify-center rounded-xl bg-white/10">
                                    <MapPinIcon class="text-gray-400 h-8 w-8 shrink-0" />
                                </span>
                            </div>
                            <div class="mt-6">
                                <h3 class="text-lg font-semibold leading-8 text-gray-400">NZ Bridal Events</h3>
                                <p class="mt-2 text-base leading-7 text-gray-400">
                                    Planner includes up to date list of NZ wedding expos and other bridal events you don't want to
                                    miss.
                                </p>
                            </div>
                        </div>

                        <div>
                            <div>
                                <span class="flex h-12 w-12 items-center justify-center rounded-xl bg-white/10">
                                    <UsersIcon class="text-gray-400 h-8 w-8 shrink-0" />
                                </span>
                            </div>
                            <div class="mt-6">
                                <h3 class="text-lg font-semibold leading-8 text-gray-400">RSVP Tracking</h3>
                                <p class="mt-2 text-base leading-7 text-gray-400">
                                    Conveniently track your guest RSVPs (and dietary requirements) from within your wedding
                                    planner.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="relative bg-gray-200">
                    <div class="h-56 bg-rose-300">
                        <img
                            class="h-full w-full object-cover"
                            :src="asset('img/landingpages/coffeeandphone.jpg')"
                            alt="coffee and cell phone"
                        />
                    </div>
                    <div class="relative w-full pb-12 pt-6 px-6">
                        <div>
                            <h2 class="text-lg font-semibold text-gray-600">NZ's best wedding planning app</h2>
                            <p class="mt-2 text-3xl font-bold tracking-tight text-rose-300">Treat yourself</p>
                            <p class="mt-3 text-lg text-gray-600">
                                Enjoy planning your dream wedding in your own beautiful personal wedding planner. It works across
                                devices.
                            </p>
                            <div class="mt-8">
                                <div class="inline-flex rounded-md shadow-sm">
                                    <Link :href="route('register')">
                                        <PrimaryButton class="text-lg">Get the app</PrimaryButton>
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="mt-8 text-gray-500">
                    <p class="text-sm">&copy; {{ new Date().getFullYear() }} WedPro Limited. All rights reserved.</p>
                    <div>
                        <Link :href="route('legal.termsandconditions')" class="font-bold">Terms of Service</Link> |
                        <Link :href="route('legal.privacypolicy')" class="font-bold">Privacy Policy</Link>
                    </div>
                </div>
            </div>
        </div>
    </Layout>
</template>
